import "react-international-phone/style.css";
import React from "react";
import {
  CountryIso2,
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";
import { Flex, Select, Text, TextField } from "@radix-ui/themes";

export interface RadixPhoneInputProps
  extends Omit<TextField.RootProps, "onChange"> {
  value: string;
  onPhoneChange: (phone: string) => void;
}

/**
 * A React functional component for a phone input field with country selection.
 * This component integrates with a custom hook `usePhoneInput` to manage phone input state
 * and provides a dropdown for selecting the country.
 *
 * @component
 * @param {RadixPhoneInputProps} props - The props for the `RadixPhoneInput` component.
 * @param {string} props.value - The current phone number value.
 * @param {(phone: string) => void} props.onPhoneChange - Callback function triggered when the phone number changes.
 * @param {object} [props.restProps] - Additional props passed to the underlying `TextField.Root` component.
 *
 * @returns {JSX.Element} A phone input field with a country selector dropdown.
 *
 * @example
 * ```tsx
 * <RadixPhoneInput
 *   value="+4512345678"
 *   onPhoneChange={(phone) => console.log(phone)}
 * />
 * ```
 */

export const RadixPhoneInput: React.FC<RadixPhoneInputProps> = ({
  value,
  onPhoneChange,
  ...restProps
}) => {
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
    usePhoneInput({
      defaultCountry: "dk",
      value,
      countries: defaultCountries,
      onChange: (data) => {
        onPhoneChange(data.phone);
      },
    });

  return (
    <TextField.Root
      placeholder="Phone number"
      value={inputValue}
      onChange={handlePhoneValueChange}
      type="tel"
      size="3"
      ref={inputRef}
      {...restProps}
    >
      <TextField.Slot>
        <Select.Root
          value={country.iso2}
          onValueChange={(e) => setCountry(e as CountryIso2)}
        >
          <Select.Trigger variant="ghost" />
          <Select.Content>
            {defaultCountries.map((c) => {
              const country = parseCountry(c);
              return (
                <Select.Item key={country.iso2} value={country.iso2}>
                  <Flex gap="3" align="center">
                    <FlagImage iso2={country.iso2} />
                    <Text size="2" weight="medium">
                      {country.iso2.toUpperCase()}
                    </Text>
                  </Flex>
                </Select.Item>
              );
            })}
          </Select.Content>
        </Select.Root>
      </TextField.Slot>
    </TextField.Root>
  );
};
